import React, { memo, useEffect, useState } from 'react'
import { CirclesGraph } from '../core/CirclesGraph'
import { LINK_ROLE_HUES } from '../core/layoutLinkedCircles'
import { GraphColorMode, LinkLegendEntry } from '../types'
import { useGraphRenderContext } from './GraphRenderContext'

interface Props {
  graph: CirclesGraph
}

// Colour of a link role's lines (legend index), grey when uncoloured
export const linkLineColor = (mode: GraphColorMode, index: number | null) =>
  index === null
    ? mode === 'light'
      ? 'hsl(200 10% 45%)'
      : 'hsl(200 10% 70%)'
    : mode === 'light'
    ? `hsl(${LINK_ROLE_HUES[index]} 60% 42%)`
    : `hsl(${LINK_ROLE_HUES[index]} 60% 65%)`

// Line thickness, relative to the space between two lines of a clasp
const lineWidthRatio = 0.14

// Linked circles view (#107): the clasps where each sub-circle touches its
// super-circle, one thick line per link role. Drawn over the discs, only
// visual: the link roles are clickable in their disc
export default memo(function DiscLinks({ graph }: Props) {
  const { colorMode } = useGraphRenderContext()
  const links = useGraphLinks(graph).links

  if (links.length === 0) return null

  return (
    <svg className="rb-graph-links" width="1" height="1" aria-hidden="true">
      {links.flatMap((link) => {
        const length = Math.hypot(link.x2 - link.x1, link.y2 - link.y1) || 1
        // Unit vector across the clasp's axis: lines side by side
        const px = -(link.y2 - link.y1) / length
        const py = (link.x2 - link.x1) / length
        return link.lines.map((line, index) => {
          const offset = (index - (link.lines.length - 1) / 2) * link.spacing
          return (
            <line
              key={`${link.id}-${index}`}
              x1={link.x1 + px * offset}
              y1={link.y1 + py * offset}
              x2={link.x2 + px * offset}
              y2={link.y2 + py * offset}
              stroke={
                link.tone === 'light'
                  ? colorMode === 'light'
                    ? 'hsl(200 10% 82%)'
                    : 'hsl(200 10% 32%)'
                  : linkLineColor(colorMode, line.colorIndex)
              }
              strokeWidth={link.width ?? link.spacing * lineWidthRatio}
              strokeLinecap="round"
            />
          )
        })
      })}
    </svg>
  )
})

// The clasps and the legend of the linked circles view, refreshed with the data
export function useGraphLinks(graph: CirclesGraph) {
  const [state, setState] = useState({
    links: graph.discLinks,
    legend: graph.linkLegend,
  })
  useEffect(() => {
    const update = () =>
      setState({ links: graph.discLinks, legend: graph.linkLegend })
    update()
    graph.on('nodesData', update)
    return () => {
      graph.off('nodesData', update)
    }
  }, [graph])
  return state
}

// Names the colours of the clasps, from the org's own link roles
export const LinkLegend = memo(function LinkLegend({ graph }: Props) {
  const { colorMode } = useGraphRenderContext()
  const legend: LinkLegendEntry[] = useGraphLinks(graph).legend
  if (legend.length === 0) return null
  return (
    <div className="rb-graph-link-legend">
      {legend.map((entry) => (
        <div key={entry.key} className="rb-graph-link-legend-entry">
          <span
            className="rb-graph-link-legend-line"
            style={{
              backgroundColor: linkLineColor(colorMode, entry.colorIndex),
            }}
          />
          <span>{entry.name}</span>
        </div>
      ))}
    </div>
  )
})
